import React, { useState } from 'react';
import PropTypes from "prop-types";
import { Radio, Typography, Row, Col, InputNumber, Select } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

const AlternateTimingUI = ({ dose, index, handleMedicationChange }) => {

  // --- Options ---
  const intervalOptions = ["4h", "6h", "8h", "12h", "48h"];
  const frequencyOptions = ["Once", "Twice", "Thrice"];

  const foodRelationOptions = [
    { label: "Before Food", value: "Before Food" },
    { label: "After Food", value: "After Food" },
    { label: "With Food", value: "With Food" },
  ];

  const specialTimingOptions = [
    { label: "Empty Stomach", value: "Empty Stomach" },
    { label: "Bedtime", value: "Bedtime" },
  ];

  // --- States ---
const defaultTiming = typeof dose?.default_timing_instruction === "object"
  && dose?.default_timing_instruction !== null
  ? dose.default_timing_instruction
  : {};

const defaultKeys = Object.keys(defaultTiming);
const defaultValues = Object.values(defaultTiming);

const [selectedInterval, setSelectedInterval] = useState(defaultKeys[0] || "");
const [selectedFrequency, setSelectedFrequency] = useState(defaultKeys[0] || "");
const [selectedFoodTiming, setSelectedFoodTiming] = useState(defaultValues[0] || "");
const [selectedSpecialTiming, setSelectedSpecialTiming] = useState(defaultValues[0] || "");


  const [customTimes, setCustomTimes] = useState(dose.custom_times || 0);
  const [customTimesUnit, setCustomTimesUnit] = useState(dose.custom_times_unit || "day");

  // Selection styles
  const SELECTED_BG = "#f0f7ff";
  const SELECTED_BORDER = "#1890ff";
  const SELECTED_COLOR = "#1890ff";
  const DEFAULT_BG = "white";
  const DEFAULT_BORDER = "#d9d9d9";
  const DEFAULT_COLOR = "inherit";

  // --- Store "key: value" in default_timing_instruction ---
  const updateTimingInstruction = (key, value) => {
    handleMedicationChange(index, "default_timing_instruction", {
      [key]: value,
    });
    // Also, handle the specific key update (interval_timing, per_day_timing, food_timing, special_timing)
    if (intervalOptions.includes(key)) {
      handleMedicationChange(index, "interval_timing", key);
      handleMedicationChange(index, "per_day_timing", ""); // Clear frequency
    } else if (frequencyOptions.includes(key)) {
      handleMedicationChange(index, "per_day_timing", key);
      handleMedicationChange(index, "interval_timing", ""); // Clear interval
    }
  };

  // --- Interval Row ---
  const handleIntervalChange = (e) => {
    const value = e.target.value;
    setSelectedInterval(value);
    setSelectedFrequency("");
    setSelectedSpecialTiming(""); // Clear other non-food timings
    setSelectedFoodTiming(""); // Clear other non-food timings
    
    // The key is the interval value itself, the value is the timing instruction (e.g., "4h": "After Food")
    const instructionValue = selectedFoodTiming || selectedSpecialTiming;
    handleMedicationChange(index, "interval_timing", value);
    handleMedicationChange(index, "per_day_timing", ""); // Clear frequency
    handleMedicationChange(index, "default_timing_instruction", { [value]: instructionValue });
  };

  // --- Frequency Row ---
  const handleFrequencyChange = (e) => {
    const value = e.target.value;
    setSelectedFrequency(value);
    setSelectedInterval("");
    setSelectedSpecialTiming(""); // Clear other non-food timings
    setSelectedFoodTiming(""); // Clear other non-food timings

    // The key is the frequency value itself, the value is the timing instruction (e.g., "Twice": "After Food")
    const instructionValue = selectedFoodTiming || selectedSpecialTiming;
    handleMedicationChange(index, "per_day_timing", value);
    handleMedicationChange(index, "interval_timing", ""); // Clear interval
    handleMedicationChange(index, "default_timing_instruction", { [value]: instructionValue });
  };
  
  // Helper to update the timing instruction key based on the currently selected Interval or Frequency
  const getActiveTimingKey = () => selectedInterval || selectedFrequency;

  // --- Food Timing (Value) ---
  const handleFoodTimingChange = (e) => {
    const value = e.target.value;
    setSelectedFoodTiming(value);
    setSelectedSpecialTiming(""); // Clear special timing if food timing is selected

    handleMedicationChange(index, "food_timing", value);
    handleMedicationChange(index, "special_timing", "");

    const key = getActiveTimingKey();
    if (key) {
      handleMedicationChange(index, "default_timing_instruction", { [key]: value });
    }
  };

  // --- Special Timing (Value) ---
  const handleSpecialTimingChange = (e) => {
    const value = e.target.value;
    setSelectedSpecialTiming(value);
    setSelectedFoodTiming(""); // Clear food timing if special timing is selected

    handleMedicationChange(index, "special_timing", value);
    handleMedicationChange(index, "food_timing", "");
    
    const key = getActiveTimingKey();
    if (key) {
      handleMedicationChange(index, "default_timing_instruction", { [key]: value });
    }
  };

  // --- Styles ---
  const cellStyle = {
    padding: '8px 0',
    border: '1px solid #f0f0f0',
    margin: '-1px 0 0 -1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: "17.2px",
    height: "32px",
    fontWeight: "bold",
  };

  const baseRadioButtonStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    borderRadius: '0',
    border: 'none',
    borderWidth: '1px', // Ensure border width is set
    borderStyle: 'solid', // Ensure border style is set
    boxShadow: 'none', // Override AntD default shadows
    transition: 'none', // Optional: disable transition for immediate style change
  };

  // Helper function to generate the dynamic style
  const getSelectionStyle = (currentValue, selectedValue) => ({
    backgroundColor: currentValue === selectedValue ? SELECTED_BG : DEFAULT_BG,
    borderColor: currentValue === selectedValue ? SELECTED_BORDER : DEFAULT_BORDER,
    color: currentValue === selectedValue ? SELECTED_COLOR : DEFAULT_COLOR,
  });


  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>

      {/* --- 1. INTERVAL TIMING --- */}
      <Radio.Group value={selectedInterval} onChange={handleIntervalChange} style={{ width: '100%' }}>
        <Row gutter={0} style={{ borderBottom: '1px solid #f0f0f0' }}>
          {intervalOptions.map(time => (
            <Col 
              key={time} 
              style={{ ...cellStyle, flex: 1, minWidth: 0, ...getSelectionStyle(time, selectedInterval) }}
            >
              <Radio.Button 
                value={time} 
                style={{
                  ...baseRadioButtonStyle,
                  // The Radio.Button border/color is handled by the Col style for visual consistency
                  backgroundColor: 'transparent',
                  borderColor: 'transparent',
                  color: getSelectionStyle(time, selectedInterval).color,
                }}
              >
                {time}
              </Radio.Button>
            </Col>
          ))}
        </Row>
      </Radio.Group>

      {/* --- 2. FREQUENCY TIMING + CUSTOM --- */}
      <Row gutter={0} style={{ borderBottom: '1px solid #f0f0f0' }}>
        <Col span={14}>
          <Radio.Group value={selectedFrequency} onChange={handleFrequencyChange} style={{ width: '100%' }}>
            <Row gutter={0}>
              {frequencyOptions.map(freq => (
                <Col 
                  key={freq} 
                  span={24 / frequencyOptions.length} 
                  style={{ ...cellStyle, ...getSelectionStyle(freq, selectedFrequency) }}
                >
                  <Radio.Button 
                    value={freq} 
                    style={{
                      ...baseRadioButtonStyle,
                      backgroundColor: 'transparent',
                      borderColor: 'transparent',
                      color: getSelectionStyle(freq, selectedFrequency).color,
                    }}
                  >
                    {freq}
                  </Radio.Button>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Col>

        {/* ... Custom Times UI (InputNumber/Select) remains the same ... */}
        <Col span={10}>
          <Row gutter={0}>
            <Col span={10} style={{ ...cellStyle, borderLeft: '1px solid #f0f0f0' }}>
              <InputNumber
                min={1}
                max={24}
                value={customTimes}
                onChange={(value) =>{ const merge= `${value} ${customTimesUnit}`;
                handleMedicationChange(index, "default_timing_instruction", { [merge]: selectedFoodTiming });
                setCustomTimes(value)
                setSelectedInterval("")
                setSelectedFrequency("")              }}
                bordered={false}
                style={{ width: '100%', textAlign: 'center' }}
              />
            </Col>

            <Col span={14} style={{ ...cellStyle, borderLeft: '1px solid #f0f0f0' }}>
              <Select
                value={customTimesUnit}
                onChange={(value) =>{ setCustomTimesUnit(value)}}
                bordered={false}
                suffixIcon={<CaretDownOutlined />}
                style={{ width: '100%' }}
                options={[
                  { value: 'a day', label: 'a day' },
                  { value: 'a week', label: 'a week' },
                  { value: 'a month', label: 'a month' }
                ]}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* --- 3. FOOD TIMING (Using standard Radio) --- */}
      <Radio.Group value={selectedFoodTiming} onChange={handleFoodTimingChange} style={{ width: '100%' }}>
        <Row gutter={0} style={{ borderBottom: '1px solid #f0f0f0' }}>
          {foodRelationOptions.map(option => (
            <Col 
              key={option.value} 
              span={24 / foodRelationOptions.length} 
              style={{ ...cellStyle, ...getSelectionStyle(option.value, selectedFoodTiming) }}
            >
              {/* Note: Standard Radio requires custom styling for the radio button itself if default AntD styling is undesired */}
              <Radio 
                value={option.value} 
                style={{ color: getSelectionStyle(option.value, selectedFoodTiming).color }}
              >
                {option.label}
              </Radio>
            </Col>
          ))}
        </Row>
      </Radio.Group>

      {/* --- 4. SPECIAL TIMING (Using standard Radio) --- */}
      <Radio.Group value={selectedSpecialTiming} onChange={handleSpecialTimingChange} style={{ width: '100%' }}>
        <Row gutter={0}>
          {specialTimingOptions.map(option => (
            <Col 
              key={option.value} 
              span={24 / specialTimingOptions.length} 
              style={{ ...cellStyle, ...getSelectionStyle(option.value, selectedSpecialTiming) }}
            >
              <Radio 
                value={option.value} 
                style={{ color: getSelectionStyle(option.value, selectedSpecialTiming).color }}
              >
                {option.label}
              </Radio>
            </Col>
          ))}
        </Row>
      </Radio.Group>

    </div>
  );
};

AlternateTimingUI.propTypes = {
  dose: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleMedicationChange: PropTypes.func.isRequired,
};

export default AlternateTimingUI;